import { TemplatesService } from './templates.service'
import {
  FetchTemplatesRequest,
  FetchTemplatesSuccess,
  FetchTemplatesFailure,
  FetchTemplateRequest,
  FetchTemplateSuccess,
  FetchTemplateFailure,
  DeleteTemplateRequest,
  DeleteTemplateSuccess,
  DeleteTemplateFailure,
  CreateTemplateRequest,
  CreateTemplateSuccess,
  CreateTemplateFailure,
  UpdateTemplateRequest,
  UpdateTemplateSuccess,
  UpdateTemplateFailure,
  DownloadTemplateRequest,
  DownloadTemplateSuccess,
  DownloadTemplateFailure,
  FetchTemplateTagsFailure,
  FetchTemplateTagsSuccess,
  FetchTemplateTagsRequest,
  FetchTestTemplateRequest,
  FetchTestTemplateSuccess,
  FetchTestTemplateFailure,
} from './templates.actions'
import { ShowSnackbar } from '../../shared/shared.actions'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import * as templateTypes from './templates.types'
import { map, switchMap, catchError, tap } from 'rxjs/operators'
import { Router } from '@angular/router'
import { downloadBlob } from '@shared/shared.utils'

@Injectable()
export class TemplatesEffects {
  constructor(
    private actions: Actions,
    private templatesService: TemplatesService,
    private router: Router,
  ) {}

  FetchTemplates$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.FETCH_TEMPLATES_REQUEST),
      switchMap((action: FetchTemplatesRequest) => {
        return this.templatesService.fetchTemplates(action.payload).pipe(
          map((result) => {
            return new FetchTemplatesSuccess(
              result.data.results,
              result.data.totalCount,
              result.data.filteredCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchTemplatesFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.FETCH_TEMPLATE_REQUEST),
      switchMap((action: FetchTemplateRequest) => {
        return this.templatesService.fetchTemplate(action.id).pipe(
          map((result) => {
            return new FetchTemplateSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchTemplateFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchTemplateTags$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.FETCH_TEMPLATE_TAGS_REQUEST),
      switchMap((action: FetchTemplateTagsRequest) => {
        return this.templatesService.fetchTemplateTags().pipe(
          map((result) => {
            return new FetchTemplateTagsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchTemplateTagsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DeleteTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.DELETE_TEMPLATE_REQUEST),
      switchMap((action: DeleteTemplateRequest) => {
        return this.templatesService.deleteTemplate(action.id).pipe(
          switchMap((result) => {
            return [new DeleteTemplateSuccess()]
          }),
          catchError((err, caught) => {
            return of(new DeleteTemplateFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  CreateTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.CREATE_TEMPLATE_REQUEST),
      switchMap((action: CreateTemplateRequest) => {
        return this.templatesService.createTemplate(action.payload).pipe(
          switchMap((result) => {
            this.router.navigate(['/admin/templates'])
            return [new CreateTemplateSuccess()]
          }),
          catchError((err, caught) => {
            return of(new CreateTemplateFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  UpdateTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.UPDATE_TEMPLATE_REQUEST),
      switchMap((action: UpdateTemplateRequest) => {
        return this.templatesService
          .updateTemplate(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              this.router.navigate(['/admin/templates'])
              return [new UpdateTemplateSuccess()]
            }),
            catchError((err, caught) => {
              return of(new UpdateTemplateFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DownloadTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.DOWNLOAD_TEMPLATE_REQUEST),
      switchMap((action: DownloadTemplateRequest) => {
        return this.templatesService.downloadTemplate(action.document).pipe(
          switchMap((result) => {
            return [new DownloadTemplateSuccess(result, action.document)]
          }),
          catchError((err, caught) => {
            return of(new DownloadTemplateFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  SaveTemplate$ = createEffect(
    () =>
      this.actions.pipe(
        ofType<DownloadTemplateSuccess>(
          templateTypes.DOWNLOAD_TEMPLATE_SUCCESS,
        ),
        tap((action) => downloadBlob(action.payload)),
      ),
    { dispatch: false },
  )

  FetchTestTemplate$ = createEffect(() =>
    this.actions.pipe(
      ofType(templateTypes.FETCH_TEST_TEMPLATE_REQUEST),
      switchMap((action: FetchTestTemplateRequest) => {
        return this.templatesService
          .fetchTestTemplate(action.file, action.params)
          .pipe(
            switchMap((result) => {
              return [new FetchTestTemplateSuccess(result.body)]
            }),
            catchError((err, caught) => {
              return of(new FetchTestTemplateFailure({ error: err }))
            }),
          )
      }),
    ),
  )
}
